/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { GiftCard } from "@lemmonhq/types";

export type GiftCardPanelProps = {
  cardInfo: GiftCard | null;
  loading: boolean;
  error: string | null;
  customAmount: string;
  currency: string;
  remaining: number;
  onCustomAmount: (value: string) => void;
  onReset: () => void;
};

/** The gift-card result column: lookup state, balance, and amount to apply. */
export const GiftCardPanel = defineComponent<GiftCardPanelProps>({
  name: "GiftCardPanel",
  props: [
    "cardInfo",
    "loading",
    "error",
    "customAmount",
    "currency",
    "remaining",
    "onCustomAmount",
    "onReset",
  ],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      if (props.loading) return null;

      if (props.error) {
        return (
          <div class="flex flex-col space-y-4 flex-1">
            <div class="alert alert-error shadow-lg">
              <span class="material-symbols-outlined">error</span>
              <span>{props.error}</span>
            </div>
            <button onClick={props.onReset} class="btn btn-ghost btn-sm w-full">
              <span class="material-symbols-outlined mr-2">refresh</span>
              {t("loyalty.modal.scan_again")}
            </button>
          </div>
        );
      }

      if (props.cardInfo) {
        const balance = Number(props.cardInfo.current_balance);
        const maxAmount = Math.min(balance, props.remaining);

        return (
          <div class="flex flex-col space-y-4 flex-1">
            {props.cardInfo.status !== "active" ? (
              <div class="alert alert-warning shadow-lg">
                <span class="material-symbols-outlined">warning</span>
                <span>{t("loyalty.rewards.gift_inactive")}</span>
              </div>
            ) : (
              <>
                <div class="alert alert-success shadow-lg">
                  <span class="material-symbols-outlined text-3xl">
                    card_giftcard
                  </span>
                  <div class="flex flex-col items-start">
                    <span class="font-bold">
                      {t("loyalty.rewards.gift_found")}
                    </span>
                    <span class="text-sm opacity-90">
                      {t("loyalty.modal.ready_to_apply")}
                    </span>
                  </div>
                </div>

                <div class="bg-base-300 rounded-xl p-4 space-y-3 border border-base-content/10">
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-base-content/60">
                      {t("loyalty.rewards.balance")}
                    </span>
                    <span class="text-xl font-bold text-success">
                      {props.currency} {balance.toFixed(2)}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-base-content/60">
                      {t("loyalty.rewards.payable")}
                    </span>
                    <span class="text-sm">
                      {props.currency} {props.remaining.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div class="space-y-1">
                  <label class="text-xs text-base-content/60 uppercase font-semibold">
                    {t("loyalty.rewards.gift_amount")} {props.currency}{" "}
                    {maxAmount.toFixed(2)}
                  </label>
                  <input
                    type="number"
                    class="input input-bordered w-full"
                    aria-label={t("loyalty.rewards.gift_amount")}
                    inputmode="decimal"
                    placeholder={`${props.currency} ${maxAmount.toFixed(2)}`}
                    value={props.customAmount}
                    onInput={(e) =>
                      props.onCustomAmount((e.target as HTMLInputElement).value)
                    }
                    min={0.01}
                    max={maxAmount}
                    step={0.01}
                  />
                </div>
              </>
            )}

            <button onClick={props.onReset} class="btn btn-ghost btn-sm w-full">
              <span class="material-symbols-outlined mr-2">refresh</span>
              {t("loyalty.modal.scan_again")}
            </button>
          </div>
        );
      }

      return null;
    };
  },
});
